import openhouseAPI from "../services/openhouse";

export type NetworkHealthStatus = "online" | "degraded" | "offline";

export interface CheckOptions {
  timeoutMs?: number;
  attempts?: number;
  degradeThresholdMs?: number;
  retryDelayMs?: number;
}

export type FailureReason =
  | "timeout"
  | "network"
  | "auth"
  | "server"
  | "unknown";

export interface NetworkHealthResult {
  status: NetworkHealthStatus;
  latencyMs: number | null;
  attemptsUsed: number;
  failureReason?: FailureReason;
}

const DEFAULT_TIMEOUT_MS = 1500;
const DEFAULT_ATTEMPTS = 2;
const DEFAULT_DEGRADE_THRESHOLD_MS = 1000;
const DEFAULT_RETRY_DELAY_MS = 400;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const classifyFailure = (error: unknown): FailureReason => {
  if (!error) {
    return "unknown";
  }

  if (error instanceof Error) {
    if (error.message === "timeout") {
      return "timeout";
    }
  }

  const axiosError = error as any;

  if (axiosError?.code === "ECONNABORTED" || axiosError?.message === "timeout") {
    return "timeout";
  }

  if (axiosError?.message === "Network Error") {
    return "network";
  }

  if (axiosError?.response) {
    const status = axiosError.response.status;
    if (status === 401 || status === 403) {
      return "auth";
    }

    if (status >= 500) {
      return "server";
    }
  }

  return "unknown";
};

const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<T> => {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(new Error("timeout"));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutHandle !== undefined) {
      clearTimeout(timeoutHandle);
    }
  }
};

export const checkOpenHouseHealth = async (
  options: CheckOptions = {}
): Promise<NetworkHealthResult> => {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    attempts = DEFAULT_ATTEMPTS,
    degradeThresholdMs = DEFAULT_DEGRADE_THRESHOLD_MS,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  } = options;

  if (attempts < 1) {
    throw new Error("attempts must be >= 1");
  }

  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    const start = Date.now();

    try {
      await withTimeout(openhouseAPI.get("/health"), timeoutMs);

      const latency = Date.now() - start;

      if (latency <= degradeThresholdMs) {
        return {
          status: "online",
          latencyMs: latency,
          attemptsUsed: attempt,
        };
      }

      return {
        status: "degraded",
        latencyMs: latency,
        attemptsUsed: attempt,
      };
    } catch (error) {
      lastError = error;

      const reason = classifyFailure(error);

      if (attempt === attempts) {
        return {
          status: "offline",
          latencyMs: null,
          attemptsUsed: attempt,
          failureReason: reason,
        };
      }

      if (retryDelayMs > 0) {
        await wait(retryDelayMs);
      }
    }
  }

  const failureReason = classifyFailure(lastError);

  return {
    status: "offline",
    latencyMs: null,
    attemptsUsed: attempts,
    failureReason,
  };
};
